import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { CircularProgress, Grid } from "@mui/material";
import Teachr from "../../Objects/models/Teachr";
import SessionDescriptionRepository, { SESSION_DESCRIPTION_MAX_LENGTH, SESSION_DESCRIPTION_MIN_LENGTH } from "../../Repositories/SessionDescriptionRepository";
import SettingsSection from "../../Components/SettingsSection";
import { SHOW_ERROR, SHOW_SUCCESS } from "../../Redux/Reducers/MessageReducers";
import { DV2 } from "../DeclarationV2/theme";
import { vocab } from "../DeclarationV2/vocabulary";
import { FieldError, Notice, fieldStyle, useDv2Theme } from "../DeclarationV2/layout/Ui";

/** Tournures d'une présentation personnelle : le texte parlerait du prof, pas du déroulement. */
const PRESENTATION_PATTERNS = [/je m'appelle/i, /je me pr[ée]sente/i, /\bmoi c'est\b/i, /\bje suis (un|une|professeur|prof|étudiant|étudiante|enseignant|enseignante|diplôm)/i, /^\s*bonjour/i, /mon nom est/i];

const EXAMPLES: Array<{ label: string; text: string }> = [
    {
        label: "Soutien scolaire",
        text: "Reprise des notions vues en classe, exercices d'application puis correction ensemble. Un point sur les devoirs à venir termine la séance.",
    },
    {
        label: "Langue vivante",
        text: "Échauffement à l'oral sur un sujet du quotidien, travail d'un point de grammaire ou de vocabulaire, puis mise en pratique par un dialogue ou un court texte. La séance se termine par un récapitulatif des expressions à retenir.",
    },
    {
        label: "Instrument de musique",
        text: "Échauffement technique et travail du son, reprise du morceau en cours passage par passage, puis lecture d'un nouvel extrait. La séance se termine par des conseils de travail pour la semaine.",
    },
];

export const looksLikePresentation = (text: string): boolean => PRESENTATION_PATTERNS.some((re) => re.test(text));

/**
 * Paramètres > Descriptions, maquette 8c : le déroulement habituel des cours,
 * repris sur chaque devis. Même enregistrement que `DescriptionsStep`
 * (SessionDescriptionRepository, bornes de longueur, retour automatique vers
 * la déclaration quand on en vient), avec le compteur, l'alerte quand le texte
 * est une présentation personnelle et des exemples à adapter.
 */
const DescriptionsV2 = ({ teachr }: { teachr: Teachr }) => {
    const { primary, fontFamily, text } = useDv2Theme();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const returnTo: string | undefined = (location.state as any)?.returnTo;

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState("");
    const [description, setDescription] = useState("");
    const [isEditing, setIsEditing] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;
        if (!teachr?.id) return undefined;
        SessionDescriptionRepository.get(teachr.id)
            .then((response) => {
                if (cancelled) return;
                const current = response?.sessionDescription?.description ?? "";
                setSaved(current);
                setDescription(current);
                if (!current || returnTo) setIsEditing(true);
            })
            .catch(() => undefined)
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teachr?.id]);

    const trimmed = description.trim();
    const tooShort = trimmed.length < SESSION_DESCRIPTION_MIN_LENGTH;
    const tooLong = trimmed.length > SESSION_DESCRIPTION_MAX_LENGTH;
    const presentation = trimmed.length > 0 && looksLikePresentation(trimmed);

    const save = () => {
        if (tooShort || tooLong) {
            setError(`La description doit contenir entre ${SESSION_DESCRIPTION_MIN_LENGTH} et ${SESSION_DESCRIPTION_MAX_LENGTH} caractères.`);
            return;
        }
        setError("");
        setSaving(true);
        SessionDescriptionRepository.save(teachr.id!, trimmed)
            .then(() => {
                setSaved(trimmed);
                setIsEditing(false);
                dispatch({ type: SHOW_SUCCESS, payload: "Votre description a bien été enregistrée." });
                if (returnTo) navigate(returnTo);
            })
            .catch(() => dispatch({ type: SHOW_ERROR, payload: "L'enregistrement de la description a échoué. Veuillez réessayer." }))
            .finally(() => setSaving(false));
    };
    const cancel = () => {
        setDescription(saved);
        setError("");
        setIsEditing(false);
    };

    if (loading) {
        return (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: 24, fontFamily, color: DV2.muted, fontSize: 14 }}>
                <CircularProgress size={18} /> Chargement…
            </div>
        );
    }

    return (
        <Grid container spacing={2}>
            <SettingsSection
                title={`Déroulement habituel de vos ${vocab.courses()}`}
                subtitle={`Repris sur chaque devis, ${vocab.theClient()} le lit avant de valider`}
                isEditing={isEditing}
                onEditClick={() => setIsEditing(true)}
                onSave={save}
                onCancel={cancel}
                loading={saving}
            >
                <Grid item xs={12}>
                    {returnTo && isEditing && (
                        <div style={{ marginBottom: 12 }}>
                            <Notice tone="info">Une fois la description enregistrée, vous serez renvoyé vers votre déclaration en cours.</Notice>
                        </div>
                    )}
                    {isEditing ? (
                        <div className="flex flex-col gap-3" style={{ fontFamily, color: text }}>
                            <textarea
                                id="dv2-session-description"
                                value={description}
                                onChange={(e) => { setDescription(e.target.value); if (error) setError(""); }}
                                rows={5}
                                aria-describedby="dv2-session-description-hint"
                                aria-invalid={!!error}
                                placeholder={EXAMPLES[0].text}
                                style={{ ...fieldStyle(!!error), minHeight: 120, resize: "vertical", lineHeight: 1.5, fontSize: 15 }}
                            />
                            <div id="dv2-session-description-hint" className="flex flex-wrap justify-between gap-2" style={{ fontSize: 12, color: DV2.muted }}>
                                <span>Décrivez ce qui se passe pendant {vocab.aCourse()}, pas qui vous êtes</span>
                                <span style={{ color: tooLong ? DV2.danger : DV2.muted }}>{trimmed.length} sur {SESSION_DESCRIPTION_MAX_LENGTH.toLocaleString("fr-FR")} caractères, {SESSION_DESCRIPTION_MIN_LENGTH} au minimum</span>
                            </div>
                            {error && <FieldError>{error}</FieldError>}
                            {presentation && (
                                <Notice tone="warning">
                                    Votre texte ressemble à une présentation personnelle. Il apparaîtrait tel quel sur vos devis. Remplacez-le par le déroulement d'une séance type.
                                </Notice>
                            )}
                            <div>
                                <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 8 }}>Exemples à adapter</div>
                                <div className="flex flex-wrap gap-2">
                                    {EXAMPLES.map((example) => (
                                        <button
                                            key={example.label}
                                            type="button"
                                            onClick={() => { setDescription(example.text); setError(""); }}
                                            style={{ fontFamily, fontSize: 13, fontWeight: 700, padding: "8px 14px", borderRadius: 999, border: "none", background: DV2.tint, color: primary, cursor: "pointer" }}
                                        >
                                            {example.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : saved ? (
                        <div className="flex flex-col gap-3">
                            <p style={{ fontSize: 15, lineHeight: 1.6, margin: 0, whiteSpace: "pre-line", fontFamily, color: text }}>{saved}</p>
                            {looksLikePresentation(saved) && (
                                <Notice tone="warning">
                                    Votre texte actuel est une présentation personnelle. Il apparaît tel quel sur vos devis. Remplacez-le par le déroulement d'une séance type.
                                </Notice>
                            )}
                        </div>
                    ) : (
                        <Notice tone="warning">Vous n'avez pas encore renseigné de description. Elle est nécessaire pour déclarer vos {vocab.courses()}.</Notice>
                    )}
                </Grid>
            </SettingsSection>
        </Grid>
    );
};

export default DescriptionsV2;
